import { jsx, jsxs } from "react/jsx-runtime";
const PRE_COLOR = "#3b82f6";
const POST_COLOR = "#8b5cf6";
const X_MIN = 40;
const X_MAX = 200;
const MU = 100;
const SIGMA = 15;
function pdf(x, mu, sigma) {
  const z = (x - mu) / sigma;
  return Math.exp(-0.5 * z * z) / (sigma * Math.sqrt(2 * Math.PI));
}
function BellCurveChart({
  preIndex,
  postIndex,
  phase,
  width = 520,
  height = 220
}) {
  const padLeft = 24;
  const padRight = 24;
  const padTop = 24;
  const padBottom = 36;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;
  const STEP = 2;
  const samples = [];
  for (let xi = X_MIN; xi <= X_MAX; xi += STEP) {
    samples.push({ x: xi, y: pdf(xi, MU, SIGMA) });
  }
  const yMax = samples.reduce((m, p) => p.y > m ? p.y : m, 0);
  const xToPx = (x) => padLeft + (x - X_MIN) / (X_MAX - X_MIN) * plotW;
  const yToPx = (y) => padTop + plotH - y / yMax * plotH;
  const baselineY = padTop + plotH;
  const areaPath = [
    `M ${xToPx(samples[0].x)} ${baselineY}`,
    ...samples.map((p) => `L ${xToPx(p.x).toFixed(2)} ${yToPx(p.y).toFixed(2)}`),
    `L ${xToPx(samples[samples.length - 1].x)} ${baselineY}`,
    "Z"
  ].join(" ");
  const outlinePath = [
    `M ${xToPx(samples[0].x)} ${yToPx(samples[0].y).toFixed(2)}`,
    ...samples.slice(1).map((p) => `L ${xToPx(p.x).toFixed(2)} ${yToPx(p.y).toFixed(2)}`)
  ].join(" ");
  const ticks = [40, 70, 85, 100, 115, 130, 160, 200];
  const clamp = (v) => Math.max(X_MIN, Math.min(X_MAX, v));
  const dots = [];
  const showPre = (phase === "pre" || phase === "comparison") && preIndex != null;
  const showPost = (phase === "post" || phase === "comparison") && postIndex != null;
  if (showPre) {
    dots.push({ x: clamp(preIndex), label: "Pre", color: PRE_COLOR, rawValue: preIndex });
  }
  if (showPost) {
    dots.push({ x: clamp(postIndex), label: "Post", color: POST_COLOR, rawValue: postIndex });
  }
  const showArrow = phase === "comparison" && preIndex != null && postIndex != null;
  const arrowColor = showArrow && postIndex > preIndex ? "#22c55e" : showArrow ? "#ef4444" : "#6b7280";
  return /* @__PURE__ */ jsx("div", { className: "w-full flex justify-center", children: /* @__PURE__ */ jsxs(
    "svg",
    {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
      role: "img",
      "aria-label": "AIQ 지수 정규분포 차트",
      className: "max-w-full",
      children: [
        /* @__PURE__ */ jsx("path", { d: areaPath, fill: "#c7d2fe", opacity: 0.35 }),
        /* @__PURE__ */ jsx("path", { d: outlinePath, fill: "none", stroke: "#6366f1", strokeWidth: 1.5, opacity: 0.7 }),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: padLeft,
            y1: baselineY,
            x2: padLeft + plotW,
            y2: baselineY,
            stroke: "#cbd5e1",
            strokeWidth: 1
          }
        ),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: xToPx(MU),
            y1: padTop,
            x2: xToPx(MU),
            y2: baselineY,
            stroke: "#94a3b8",
            strokeWidth: 1,
            strokeDasharray: "3,3",
            opacity: 0.6
          }
        ),
        ticks.map((t) => {
          const px = xToPx(t);
          const isMu = t === MU;
          return /* @__PURE__ */ jsxs("g", { children: [
            /* @__PURE__ */ jsx(
              "line",
              {
                x1: px,
                y1: baselineY,
                x2: px,
                y2: baselineY + 4,
                stroke: "#94a3b8",
                strokeWidth: 1
              }
            ),
            /* @__PURE__ */ jsx(
              "text",
              {
                x: px,
                y: baselineY + 16,
                fontSize: 10,
                fill: isMu ? "#475569" : "#94a3b8",
                textAnchor: "middle",
                fontWeight: isMu ? "bold" : "normal",
                children: t
              }
            )
          ] }, t);
        }),
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
          "marker",
          {
            id: "bellArrow",
            markerWidth: "8",
            markerHeight: "6",
            refX: "8",
            refY: "3",
            orient: "auto",
            children: /* @__PURE__ */ jsx("polygon", { points: "0 0, 8 3, 0 6", fill: arrowColor })
          }
        ) }),
        showArrow && (() => {
          const preX = xToPx(clamp(preIndex));
          const postX = xToPx(clamp(postIndex));
          const arrowY = baselineY - 6;
          return /* @__PURE__ */ jsx(
            "line",
            {
              x1: preX,
              y1: arrowY,
              x2: postX,
              y2: arrowY,
              stroke: arrowColor,
              strokeWidth: 1.5,
              markerEnd: "url(#bellArrow)",
              opacity: 0.8
            }
          );
        })(),
        dots.map((d, i) => {
          const dx = xToPx(d.x);
          const dy = yToPx(pdf(d.x, MU, SIGMA));
          const labelOffset = i === 0 ? -14 : -26;
          return /* @__PURE__ */ jsxs("g", { children: [
            /* @__PURE__ */ jsx(
              "line",
              {
                x1: dx,
                y1: dy,
                x2: dx,
                y2: baselineY,
                stroke: d.color,
                strokeWidth: 1,
                strokeDasharray: "2,2",
                opacity: 0.5
              }
            ),
            /* @__PURE__ */ jsx("circle", { cx: dx, cy: dy, r: 6, fill: d.color, opacity: 0.95 }),
            /* @__PURE__ */ jsxs(
              "text",
              {
                x: dx,
                y: dy + labelOffset,
                fontSize: 11,
                fontWeight: "bold",
                fill: d.color,
                textAnchor: "middle",
                children: [
                  d.label,
                  " ",
                  Math.round(d.rawValue)
                ]
              }
            )
          ] }, d.label);
        })
      ]
    }
  ) });
}
export {
  BellCurveChart
};
//# sourceMappingURL=BellCurveChart.js.map
